import {
    Alert,
    Badge,
    Box,
    Button,
    Collapse,
    Group,
    Modal,
    ScrollArea,
    Stack,
    Text,
    Tooltip,
    UnstyledButton,
} from '@mantine/core'
import {useDisclosure} from '@mantine/hooks'
import {
    IconAlertTriangle,
    IconChevronDown,
    IconChevronRight,
    IconHelpCircle,
} from '@tabler/icons-react'
import clsx from 'clsx'
import {format, parseISO} from 'date-fns'

import type {ChangePreviewDelta, MantineColorName} from 'src/theme/semantic-colors'
import {
    changePreviewDeltaColor,
    changePreviewReasonColor,
    scheduleStageColor,
    signalAttentionColor,
} from 'src/theme/semantic-colors'

import classes from './change-preview.module.css'
import {
    buildPreviewTimeline,
    previewApplyAction,
    type PreviewTimelineEntry,
    type ScheduleChangeSummaryItem,
} from './change-preview-logic'
import {
    PREVIEW_DELTA_DETAILS,
    PREVIEW_OUTCOME_LABELS,
    previewOutcomeDelta,
    previewRowAnnotation,
    previewRowDetail,
    SCHEDULE_ROLE_LABELS,
} from './preview-labels'
import type {ChangePreview, PreviewOutcome, PreviewRow, UnchangedReason} from './schemas'

type ChangePreviewModalProps = {
    opened: boolean
    title: string
    preview: ChangePreview | null
    /** The edit being previewed, shown as before → after above the ripple it causes. */
    changes?: ScheduleChangeSummaryItem[]
    loading?: boolean
    applying?: boolean
    onClose: () => void
    onApply: () => void
}

function rowColor(outcome: PreviewOutcome, reason: UnchangedReason | null): MantineColorName {
    if (outcome === 'unchanged' && reason != null) {
        return changePreviewReasonColor(reason)
    }

    return changePreviewDeltaColor(previewOutcomeDelta(outcome))
}

/**
 * Every status tag carries its own definition on hover, marked by the question mark, so the
 * modal needs no legend.
 */
function HelpBadge({
    label,
    detail,
    color,
    size,
    variant = 'light',
    dimmed = false,
}: {
    label: string
    detail: string
    color: MantineColorName | 'gray'
    size: 'sm' | 'md'
    variant?: 'light' | 'outline'
    dimmed?: boolean
}) {
    return (
        <Tooltip label={detail} multiline w={260} withArrow position="top" openDelay={120}>
            <Badge
                variant={variant}
                color={color}
                c={dimmed ? 'dimmed' : undefined}
                size={size}
                className={classes['helpBadge']}
                rightSection={<IconHelpCircle size={size === 'sm' ? 11 : 13} stroke={1.8} />}
            >
                {label}
            </Badge>
        </Tooltip>
    )
}

function CountBadge({
    delta,
    count,
    label,
}: {
    delta: ChangePreviewDelta
    count: number
    label: string
}) {
    return (
        <HelpBadge
            label={label}
            detail={PREVIEW_DELTA_DETAILS[delta]}
            color={count > 0 ? changePreviewDeltaColor(delta) : 'gray'}
            variant={count > 0 ? 'light' : 'outline'}
            dimmed={count === 0}
            size="md"
        />
    )
}

function ChangeSummary({changes}: {changes: ScheduleChangeSummaryItem[]}) {
    return (
        <div className={classes['changeSummary']}>
            {changes.map((change) => (
                <Box key={change.label}>
                    <Text className={classes['changeLabel']}>{change.label}</Text>
                    <Group gap={6} wrap="nowrap">
                        {change.from != null && (
                            <Text size="sm" c="dimmed" className={classes['changeFrom']}>
                                {change.from}
                            </Text>
                        )}
                        <Text size="sm" fw={700}>
                            {change.from == null ? change.to : `→ ${change.to}`}
                        </Text>
                    </Group>
                </Box>
            ))}
        </div>
    )
}

function TimelineRow({row}: {row: PreviewRow}) {
    const removed = row.outcome === 'removed'
    const quiet = row.outcome === 'unchanged' || removed

    return (
        <div className={classes['row']}>
            <Box
                className={clsx(classes['dot'], removed && classes['dotHollow'])}
                bg={scheduleStageColor(row.role)}
            />
            <div className={classes['rowDate']}>
                <Text
                    size="sm"
                    fw={quiet ? 500 : 600}
                    c={quiet ? 'dimmed' : undefined}
                    td={removed ? 'line-through' : undefined}
                >
                    {SCHEDULE_ROLE_LABELS[row.role]} ·{' '}
                    {format(parseISO(row.startDate), 'EEE, MMM d')}
                </Text>
            </div>
            <HelpBadge
                label={previewRowAnnotation(row.outcome, row.reason)}
                detail={previewRowDetail(row.outcome, row.reason)}
                color={rowColor(row.outcome, row.reason)}
                size="sm"
            />
        </div>
    )
}

function CollapsedRun({entry}: {entry: Extract<PreviewTimelineEntry, {kind: 'run'}>}) {
    const [expanded, {toggle}] = useDisclosure(false)

    return (
        <>
            <UnstyledButton className={classes['runRow']} onClick={toggle} w="100%">
                <span className={classes['runRail']}>⋮</span>
                <Text size="xs" c="dimmed" fs="italic" flex={1} ta="left">
                    {entry.summary}
                </Text>
                <Text size="xs" c="dimmed">
                    {expanded ? 'Hide' : 'Show'}
                </Text>
                {expanded ? (
                    <IconChevronDown size={14} color="var(--mantine-color-dimmed)" />
                ) : (
                    <IconChevronRight size={14} color="var(--mantine-color-dimmed)" />
                )}
            </UnstyledButton>
            <Collapse expanded={expanded}>
                {expanded
                    ? entry.rows.map((row) => (
                          <TimelineRow
                              key={`${row.role}-${row.startDate}-${row.jobId ?? 'new'}`}
                              row={row}
                          />
                      ))
                    : null}
            </Collapse>
        </>
    )
}

export function ChangePreviewModal({
    opened,
    title,
    preview,
    changes = [],
    loading = false,
    applying = false,
    onClose,
    onApply,
}: ChangePreviewModalProps) {
    const timeline = preview == null ? [] : buildPreviewTimeline(preview.rows)
    const applyAction = preview == null ? null : previewApplyAction(preview)

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={title}
            size="lg"
            transitionProps={{duration: 0}}
        >
            <Stack gap="md">
                {loading || preview == null || applyAction == null ? (
                    <Text size="sm" c="dimmed">
                        Loading preview…
                    </Text>
                ) : (
                    <>
                        {changes.length > 0 && <ChangeSummary changes={changes} />}

                        <Group gap={6}>
                            <CountBadge
                                delta="added"
                                count={preview.createdCount}
                                label={`+${preview.createdCount} added`}
                            />
                            <CountBadge
                                delta="updated"
                                count={preview.updatedCount}
                                label={`${preview.updatedCount} updated`}
                            />
                            <CountBadge
                                delta="removed"
                                count={preview.removedCount}
                                label={`−${preview.removedCount} removed`}
                            />
                            {preview.unlinkedCount > 0 && (
                                <CountBadge
                                    delta="kept"
                                    count={preview.unlinkedCount}
                                    label={`${preview.unlinkedCount} kept`}
                                />
                            )}
                            <CountBadge
                                delta="unchanged"
                                count={preview.unchangedCount}
                                label={`${preview.unchangedCount} unchanged`}
                            />
                        </Group>

                        {preview.unlinkedCount > 0 && (
                            <Alert
                                variant="light"
                                color={signalAttentionColor()}
                                icon={<IconAlertTriangle size={16} />}
                                title={`${preview.unlinkedCount} kept on the board`}
                            >
                                <Text size="xs">
                                    These stay on the board but leave the schedule. Cancel them
                                    individually if they shouldn&apos;t run.
                                </Text>
                            </Alert>
                        )}

                        {preview.pickupMoveBlocked && (
                            <Alert
                                variant="light"
                                color={signalAttentionColor()}
                                icon={<IconAlertTriangle size={16} />}
                                title="Pickup stays where it is"
                            >
                                <Text size="xs">
                                    It has already started or has logged work, so the new end date
                                    does not move it.
                                </Text>
                            </Alert>
                        )}

                        <ScrollArea.Autosize mah={340} type="auto" offsetScrollbars>
                            <div className={classes['timeline']}>
                                {timeline.map((entry) =>
                                    entry.kind === 'row' ? (
                                        <TimelineRow key={entry.key} row={entry.row} />
                                    ) : (
                                        <CollapsedRun key={entry.key} entry={entry} />
                                    ),
                                )}
                            </div>
                        </ScrollArea.Autosize>

                        <div className={classes['footer']}>
                            <Text size="xs" c="dimmed">
                                Worked and past visits keep what they were scheduled under.
                            </Text>
                            <Group gap="sm">
                                <Button variant="default" onClick={onClose} disabled={applying}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={onApply}
                                    loading={applying}
                                    disabled={applyAction.empty}
                                    color={
                                        applyAction.destructive
                                            ? changePreviewDeltaColor('removed')
                                            : undefined
                                    }
                                >
                                    {applyAction.label}
                                </Button>
                            </Group>
                        </div>
                    </>
                )}
            </Stack>
        </Modal>
    )
}

export {PREVIEW_OUTCOME_LABELS}
